import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import ItemAvatar from "../../../components/ItemAvatar";
import { formatIDR } from "../../../utils/helpers/formatIDR";
import "../../PurchasePage/FormPurchase/style.css";

const DETAIL_META = {
  cogs: { title: "Rincian HPP Historis", description: "HPP saat transaksi terjadi, bukan HPP resep saat ini. Transaksi draft dan batal tidak dihitung.", column: "Produk", amount: "Total HPP" },
  operating_costs: { title: "Rincian Biaya Operasional", description: "Biaya yang masih aktif pada periode ini, termasuk biaya dari pembelian lama. Tidak termasuk penyusutan dan biaya batal.", column: "Biaya / Referensi", amount: "Total biaya" },
  depreciation: { title: "Rincian Penyusutan", description: "Penyusutan aktif yang sudah jatuh tempo pada periode terpilih. Jadwal masa depan dan pembatalan tidak dihitung.", column: "Aset", amount: "Total penyusutan" },
};

const ProfitDetailModal = ({ type, detail, loading, error, rangeLabel, onClose, onRetry, onPageChange }) => {
  const dialogRef = useRef(null);
  const open = Boolean(type);
  useEffect(() => {
    if (!open) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key !== "Tab") return;
      const focusable = [...dialogRef.current.querySelectorAll('button:not([disabled]), [href], [tabindex="0"]')];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const outsideDialog = !dialogRef.current.contains(document.activeElement);
      if (event.shiftKey && (outsideDialog || document.activeElement === first || document.activeElement === dialogRef.current)) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && (outsideDialog || document.activeElement === last || document.activeElement === dialogRef.current)) {
        event.preventDefault(); first?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      previousFocus?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;
  const meta = DETAIL_META[type];
  const ready = detail?.type === type && !loading;
  const items = ready ? detail.items || [] : [];
  const itemLabel = (item) => {
    if (type === "cogs" && item.group_name && item.group_name !== item.label) return `${item.group_name} · ${item.label || "Varian"}`;
    if (type === "operating_costs" && item.category === "Biaya dari pembelian lama") return `Pembelian #${item.label}`;
    return item.label || `${meta.column} #${item.id}`;
  };
  const dateLabel = (value) => {
    const date = new Date(String(value || "").replace(" ", "T"));
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });
  };

  return createPortal(
    <div className="modal modal-blur show d-block dashboard-profit-modal" role="presentation" style={{ backgroundColor: "rgba(0, 0, 0, 0.3)", zIndex: 1090 }} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
        <section className="modal-content purchase-modal-content" role="dialog" aria-modal="true" aria-labelledby="dashboard-profit-detail-title" tabIndex={-1} ref={dialogRef}>
          <div className="modal-header purchase-modal-header">
            <div className="d-flex align-items-center gap-3">
              <span className="purchase-modal-icon bg-primary-lt text-primary" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M5 3h14v18l-3-2-2 2-2-2-2 2-2-2-3 2zM9 8h6M9 12h6M9 16h3" /></svg></span>
              <div><span className="text-secondary small text-uppercase fw-semibold">Rekonsiliasi laba</span><h3 className="modal-title" id="dashboard-profit-detail-title">{meta.title}</h3></div>
            </div>
            <button type="button" className="btn-close" aria-label="Tutup rincian laba" onClick={onClose} />
          </div>
          <div className="modal-body purchase-modal-body">
            <p className="small text-secondary">{rangeLabel}</p>
            {error ? <div className="alert alert-danger mb-0" role="alert"><p>{error}</p><button type="button" className="btn btn-primary btn-sm" onClick={onRetry}>Coba lagi</button></div>
              : !ready ? <div className="text-center py-5" role="status"><span className="spinner-border text-primary" /><span className="visually-hidden">Memuat rincian laba</span></div>
              : <>
                <div className="dashboard-profit-total"><span className="text-secondary">{meta.amount}</span><strong>{formatIDR(detail.total_amount)}</strong><small className="text-secondary">{Number(detail.total_items || 0).toLocaleString("id-ID")} {type === "cogs" ? "produk" : "catatan"} · seluruh halaman</small></div>
                <div className="table-responsive mt-3">
                  <table className="table table-vcenter mb-0">
                    <thead><tr><th>{meta.column}</th><th className={type === "cogs" ? "text-end" : ""}>{type === "cogs" ? "Terjual" : "Tanggal"}</th><th className="text-end">Jumlah</th></tr></thead>
                    <tbody>{items.length ? items.map((item, index) => <tr key={`${item.id}-${index}`}>
                      <td><div className="d-flex align-items-center gap-2">
                        {type === "cogs" ? <ItemAvatar name={itemLabel(item)} image={item.image} size="sm" /> : null}
                        <div><strong>{itemLabel(item)}</strong><small className="d-block text-secondary">{item.category}</small></div>
                      </div></td>
                      <td className={type === "cogs" ? "text-end text-nowrap" : "text-nowrap"}>{type === "cogs" ? `${Number(item.quantity || 0).toLocaleString("id-ID", { maximumFractionDigits: 4 })} ${item.unit || ""}` : dateLabel(item.occurred_at)}</td>
                      <td className="text-end text-nowrap fw-semibold">{formatIDR(item.amount)}</td>
                    </tr>) : <tr><td colSpan={3} className="text-center text-secondary py-4">Belum ada {type === "cogs" ? "HPP" : "biaya"} tercatat pada periode ini.</td></tr>}</tbody>
                  </table>
                </div>
                {detail.last_page > 1 ? <nav className="d-flex align-items-center justify-content-between gap-2 mt-3" aria-label="Halaman rincian laba">
                  <button type="button" className="btn btn-sm btn-outline-primary" disabled={detail.current_page <= 1} onClick={() => onPageChange(detail.current_page - 1)}>Sebelumnya</button>
                  <span className="small text-secondary">{detail.current_page} / {detail.last_page}</span>
                  <button type="button" className="btn btn-sm btn-outline-primary" disabled={detail.current_page >= detail.last_page} onClick={() => onPageChange(detail.current_page + 1)}>Berikutnya</button>
                </nav> : null}
              </>}
            <p className="text-secondary small mt-3 mb-0">{meta.description}</p>
          </div>
          <div className="modal-footer purchase-modal-footer"><button type="button" className="btn btn-primary ms-auto" onClick={onClose}>Tutup</button></div>
        </section>
      </div>
    </div>, document.body,
  );
};

export default ProfitDetailModal;
